import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, dateFmt, dateTimeFmt, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Wallet2, HandCoins, ReceiptText, Loader2 } from "lucide-react";

export default function Receivables() {
  const { user } = useAuth();
  const { branches } = useBranch();
  const branchOptions = useMemo(() => branches.filter((b) => b.active !== false), [branches]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);   // {customer, credit_sales, payments, ...}
  const [search, setSearch] = useState("");

  const [payOpen, setPayOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [payBranch, setPayBranch] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [bukti, setBukti] = useState(null);     // payment -> bukti

  const load = () => {
    setLoading(true);
    client.get("/receivables").then((r) => setRows(r.data))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openDetail = (cid) => {
    client.get(`/receivables/${cid}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiError(e)));
  };

  const openPay = () => {
    setAmount(String(Math.round(detail.outstanding)));
    setPayBranch(detail.credit_sales[0]?.branch_id || branchOptions[0]?.id || "");
    setNote("");
    setPayOpen(true);
  };

  const doPay = async () => {
    if (busy) return;
    if (!amount || Number(amount) <= 0) { toast.error("Isi jumlah pembayaran"); return; }
    if (!payBranch) { toast.error("Pilih cabang penerima"); return; }
    setBusy(true);
    try {
      const r = await client.post(`/receivables/${detail.customer.id}/payments`, {
        branch_id: payBranch, amount: Number(amount), note,
      });
      toast.success("Pembayaran tercatat");
      setPayOpen(false); setBukti(r.data);
      openDetail(detail.customer.id); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const filtered = rows.filter((r) => !search || r.customer_name.toLowerCase().includes(search.toLowerCase()));
  const totalOutstanding = rows.reduce((s, r) => s + r.outstanding, 0);

  return (
    <div className="p-6 space-y-5 animate-in-up" data-testid="receivables-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <Wallet2 className="h-7 w-7 text-amber-600" /> Piutang Pelanggan
        </h1>
        <p className="text-sm text-muted-foreground">Kas bon pelanggan, pembayaran bulanan, dan bukti pembayaran.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="p-5"><div className="text-sm text-muted-foreground">Total Piutang</div><div className="mt-1 font-mono text-2xl font-bold text-amber-600" data-testid="rcv-total">{rupiah(totalOutstanding)}</div></Card>
        <Card className="p-5"><div className="text-sm text-muted-foreground">Pelanggan Berpiutang</div><div className="mt-1 font-mono text-2xl font-bold">{rows.length}</div></Card>
      </div>

      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari pelanggan..." className="w-64" data-testid="rcv-search" />

      <div className="rounded-lg border bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Pelanggan</TableHead><TableHead>Telp</TableHead>
            <TableHead className="text-right">Kas Bon</TableHead>
            <TableHead className="text-right">Terbayar</TableHead>
            <TableHead className="text-right">Sisa Piutang</TableHead>
            <TableHead className="text-right">Limit</TableHead>
            <TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>}
            {!loading && filtered.map((r) => (
              <TableRow key={r.customer_id} data-testid={`rcv-row-${r.customer_id}`}>
                <TableCell className="font-medium">{r.customer_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.phone || "—"}</TableCell>
                <TableCell className="text-right font-mono">{rupiah(r.total_credit)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-600">{rupiah(r.total_paid)}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-amber-700" data-testid={`rcv-outstanding-${r.customer_id}`}>{rupiah(r.outstanding)}</TableCell>
                <TableCell className="text-right font-mono text-xs text-muted-foreground">{r.credit_limit > 0 ? rupiah(r.credit_limit) : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => openDetail(r.customer_id)} data-testid={`rcv-detail-${r.customer_id}`}><ReceiptText className="mr-1 h-3.5 w-3.5" /> Detail</Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && !filtered.length && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Tidak ada piutang</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* DETAIL */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl w-[95vw]" data-testid="rcv-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.customer?.name}</DialogTitle>
            <DialogDescription>{detail?.customer?.phone || "tanpa telepon"}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Total Kas Bon</div><div className="font-mono font-semibold">{rupiah(detail.total_credit)}</div></div>
                <div><div className="text-xs text-muted-foreground">Terbayar</div><div className="font-mono font-semibold text-emerald-600">{rupiah(detail.total_paid)}</div></div>
                <div><div className="text-xs text-muted-foreground">Sisa</div><div className="font-mono font-bold text-amber-700" data-testid="rcv-detail-outstanding">{rupiah(detail.outstanding)}</div></div>
              </div>

              <div className="flex justify-end">
                <Button size="sm" onClick={openPay} disabled={detail.outstanding <= 0} data-testid="rcv-pay-btn"><HandCoins className="mr-1 h-4 w-4" /> Catat Pembayaran</Button>
              </div>

              <div>
                <div className="mb-1 text-xs font-semibold text-muted-foreground">Transaksi Kas Bon</div>
                <div className="max-h-64 overflow-y-auto rounded border divide-y">
                  {detail.credit_sales.map((s) => (
                    <div key={s.id} className="p-2.5" data-testid={`rcv-creditsale-${s.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-mono text-xs">{s.number}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{dateTimeFmt(s.created_at)}</span>
                          <span className="ml-2 text-xs text-muted-foreground">· {s.branch_name}</span>
                        </div>
                        <span className="shrink-0 font-mono text-sm font-semibold">{rupiah(s.total)}</span>
                      </div>
                      <ul className="mt-1.5 space-y-0.5" data-testid={`rcv-items-${s.id}`}>
                        {(s.items || []).map((it, idx) => (
                          <li key={idx} className="flex items-start justify-between gap-2 text-xs">
                            <span className="text-slate-700">
                              <span className="font-mono text-muted-foreground">{it.qty}×</span> {it.name}
                              <span className="text-muted-foreground"> ({it.unit})</span>
                            </span>
                            <span className="shrink-0 font-mono text-muted-foreground">{rupiah(it.subtotal)}</span>
                          </li>
                        ))}
                        {!(s.items || []).length && <li className="text-xs text-muted-foreground">Tidak ada rincian item</li>}
                      </ul>
                    </div>
                  ))}
                  {!detail.credit_sales.length && <div className="py-4 text-center text-xs text-muted-foreground">—</div>}
                </div>
              </div>

              <div>
                <div className="mb-1 text-xs font-semibold text-muted-foreground">Riwayat Pembayaran</div>
                <div className="max-h-40 overflow-y-auto rounded border">
                  <Table>
                    <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Tanggal</TableHead><TableHead>Oleh</TableHead><TableHead className="text-right">Jumlah</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {detail.payments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-mono text-xs">{p.number}</TableCell>
                          <TableCell className="text-xs">{dateFmt(p.date)}</TableCell>
                          <TableCell className="text-xs">{p.created_by_name}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">{rupiah(p.amount)}</TableCell>
                          <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setBukti(p)} data-testid={`rcv-bukti-${p.id}`}><ReceiptText className="h-3.5 w-3.5" /></Button></TableCell>
                        </TableRow>
                      ))}
                      {!detail.payments.length && <TableRow><TableCell colSpan={5} className="py-4 text-center text-xs text-muted-foreground">Belum ada pembayaran</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RECORD PAYMENT */}
      <Dialog open={payOpen} onOpenChange={(o) => !o && setPayOpen(false)}>
        <DialogContent data-testid="rcv-pay-dialog">
          <DialogHeader>
            <DialogTitle>Catat Pembayaran — {detail?.customer?.name}</DialogTitle>
            <DialogDescription>Sisa piutang {rupiah(detail?.outstanding || 0)} · metode tunai (masuk setoran kas)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Jumlah Pembayaran</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="rcv-pay-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Cabang Penerima (kas)</Label>
              <Select value={payBranch} onValueChange={setPayBranch}>
                <SelectTrigger data-testid="rcv-pay-branch"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent>{branchOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="opsional" data-testid="rcv-pay-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Batal</Button>
            <Button onClick={doPay} disabled={busy} data-testid="rcv-pay-confirm">{busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menyimpan…</> : "Simpan & Cetak Bukti"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BUKTI PEMBAYARAN */}
      <Dialog open={!!bukti} onOpenChange={(o) => !o && setBukti(null)}>
        <DialogContent data-testid="rcv-bukti-dialog">
          <DialogHeader><DialogTitle>Bukti Pembayaran {bukti?.number}</DialogTitle></DialogHeader>
          {bukti && (
            <Card className="p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Pelanggan</span><span className="font-medium">{bukti.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tanggal</span><span>{dateFmt(bukti.date)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Cabang</span><span>{bukti.branch_name || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Metode</span><span>Tunai</span></div>
              {bukti.note && <div className="flex justify-between"><span className="text-muted-foreground">Catatan</span><span>{bukti.note}</span></div>}
              <div className="flex justify-between border-t pt-2 text-base"><span className="font-semibold">Jumlah</span><span className="font-mono font-bold text-emerald-700">{rupiah(bukti.amount)}</span></div>
              <div className="text-xs text-muted-foreground">Diterima oleh {bukti.created_by_name} · {dateTimeFmt(bukti.created_at)}</div>
            </Card>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBukti(null)}>Tutup</Button>
            {bukti && <Button onClick={() => window.print()} data-testid="rcv-bukti-print"><ReceiptText className="mr-2 h-4 w-4" /> Cetak</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {bukti && (
        <div id="receipt-print" className="hidden print:block">
          <div style={{ fontFamily: "monospace" }}>
            <div style={{ textAlign: "center", fontWeight: 700 }}>BUKTI PEMBAYARAN PIUTANG</div>
            <div style={{ textAlign: "center" }}>{bukti.number}</div>
            <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>Pelanggan</span><span>{bukti.customer_name}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>Tanggal</span><span>{dateFmt(bukti.date)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>Cabang</span><span>{bukti.branch_name || "-"}</span></div>
            {bukti.note ? <div style={{ fontSize: "8px" }}>Catatan: {bukti.note}</div> : null}
            <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}><span>JUMLAH</span><span>{rupiah(bukti.amount)}</span></div>
            <div style={{ fontSize: "8px", marginTop: "4px" }}>Diterima: {bukti.created_by_name}</div>
          </div>
        </div>
      )}
    </div>
  );
}
